import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import type { IncidentDetectionLapseReview } from "../../../api/incidentDetectionLapseReview";
import { BaseDataTableSaveFooter } from "../../../components/ui/BaseDataTableFrame";

import { upsertIncidentDetectionLapseReview } from "../../incidentDetectionLapseReview/logic/thunk";
import { selectIncidentDetectionLapseReview } from "../../incidentDetectionLapseReview/logic/slice";
import { showErrorToast } from "../../../lib/toast";

import ControlReturnsCard from "./ControlReturnsCard";

export const ControlReturnsForm: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const incidentDetectionLapseReview = useAppSelector(selectIncidentDetectionLapseReview);

  const [draft, setDraft] = useState<IncidentDetectionLapseReview | null>(
    incidentDetectionLapseReview,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(incidentDetectionLapseReview);
  }, [incidentDetectionLapseReview]);

  const hasChanges = JSON.stringify(draft) !== JSON.stringify(incidentDetectionLapseReview);

  const isValid =
    draft !== null &&
    (draft.controlling_office_could_detect_incident !== true || !!draft.controlling_office_detection_remarks?.trim()) &&
    (draft.irregularities_reported !== true || !!draft.irregularities_reported_remarks?.trim()) &&
    (draft.information_system_improvement_required !== true || !!draft.information_system_improvement_remarks?.trim());

  const updateDraft = (updates: Partial<IncidentDetectionLapseReview>) => {
    setDraft((current) => ({
      ...(current ?? {}),
      ...updates,
    } as IncidentDetectionLapseReview));
  };

  const handleCancel = () => setDraft(incidentDetectionLapseReview);

  const handleSave = async () => {
    if (!caseId || !draft || !hasChanges || !isValid || isSaving) return;
    setIsSaving(true);

    try {
      await dispatch(
        upsertIncidentDetectionLapseReview({
          caseId,
          data: draft,
        })
      ).unwrap();
    } catch {
      showErrorToast("Failed to update incident detection lapse review. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-1 font-[Inter]">
          <span className="text-[12px] font-medium leading-none text-[#0A0A0A]">
            Control Returns and irregularities, if any
          </span>

          <span className="text-[12px] font-normal leading-none text-[#737373]">
            Mention whether the branch’s control returns could have flagged this
            incident earlier - and note the specific returns or lapses behind each
            finding.
          </span>
        </div>

        <div className="flex flex-col">
          <ControlReturnsCard
            disabled={isSaving}
            label="Whether the controlling office could detect the incident by scrutiny of control returns submitted by the Branch?"
            value={draft?.controlling_office_could_detect_incident}
            remarks={draft?.controlling_office_detection_remarks}
            onValueChange={(value) =>
              updateDraft({
                controlling_office_could_detect_incident: value,
              })
            }
            onRemarksChange={(remarks) =>
              updateDraft({
                controlling_office_detection_remarks: remarks,
              })
            }
          />

          <ControlReturnsCard
            disabled={isSaving}
            label="Whether irregularities were reported in the control returns?"
            value={draft?.irregularities_reported}
            remarks={draft?.irregularities_reported_remarks}
            onValueChange={(value) =>
              updateDraft({ irregularities_reported: value })
            }
            onRemarksChange={(remarks) =>
              updateDraft({ irregularities_reported_remarks: remarks })
            }
          />

          <ControlReturnsCard
            disabled={isSaving}
            label="Whether there is a need to improve the existing Information System?"
            value={draft?.information_system_improvement_required}
            remarks={draft?.information_system_improvement_remarks}
            onValueChange={(value) =>
              updateDraft({
                information_system_improvement_required: value,
              })
            }
            onRemarksChange={(remarks) =>
              updateDraft({ information_system_improvement_remarks: remarks })
            }
          />
        </div>
      </div>

      {hasChanges && (
        <BaseDataTableSaveFooter
          isSaving={isSaving}
          disabled={!isValid}
          onSave={handleSave}
          onDiscard={handleCancel}
        />
      )}
    </div>
  );
};

export default ControlReturnsForm;
